import React, { useMemo } from 'react';
import { EdiBucket, bucketKey } from '../services/pendingEdiOrders.service';
import { urgencyChipClass, worstUrgency } from '../utils/ediUrgency';

// =============================================================================
// EdiBucketPanel
// =============================================================================
// An LC-dashboard-style panel of clickable Pending EDI Order buckets, but with the
// extra columns the count-only CountPanel can't show (Bill To · CSR · count). Purely
// presentational: it takes the bucket rows + reports a click via onSelect. Buckets are
// grouped by Bill To and nested by edihandle — one edihandle usually maps to one Bill
// To, but a Bill To can receive orders from several EDI handles, in which case the Bill
// To is a header and its handles are the clickable leaves underneath.
//
// Reused on the Pending EDI Orders page and (fed a ?lc_username-filtered bucket list)
// on the LC Dashboard, where an LC sees only the buckets they're a CSR on.

export interface EdiBucketPanelProps {
  buckets: EdiBucket[];
  loading?: boolean;
  onSelect: (edihandle: string, billto: number) => void;
  /** `${edihandle}|${billto}` of the open bucket, for highlighting. */
  selectedKey?: string | null;
  title?: string;
  className?: string;
  /** Suppress the panel's own frame + title bar + inner scroll so a host tile
   *  (e.g. the LC Dashboard's DashboardTile) supplies the chrome. Mirrors CountPanel. */
  embedded?: boolean;
}

interface Group {
  billto: number;
  billtoname: string;
  total: number;
  rows: EdiBucket[];
}

const EdiBucketPanel: React.FC<EdiBucketPanelProps> = ({
  buckets,
  loading = false,
  onSelect,
  selectedKey,
  title = 'Pending EDI Orders',
  className,
  embedded = false,
}) => {
  const { groups, total } = useMemo(() => {
    const byBillto = new Map<number, Group>();
    for (const b of buckets) {
      let g = byBillto.get(b.billto);
      if (!g) {
        g = { billto: b.billto, billtoname: b.billtoname || '(no bill to)', total: 0, rows: [] };
        byBillto.set(b.billto, g);
      }
      g.rows.push(b);
      g.total += b.count;
    }
    const groups = [...byBillto.values()].sort((a, b) => a.billtoname.localeCompare(b.billtoname));
    for (const g of groups) g.rows.sort((a, b) => a.edihandle.localeCompare(b.edihandle));
    const total = buckets.reduce((s, b) => s + b.count, 0);
    return { groups, total };
  }, [buckets]);

  // Count is a FIXED-width, right-justified column. Each row is its own grid, so an
  // `auto` count column would size to that row's digit count and shift the Bill To / LC
  // columns a few px per row (the "uneven spacing"). A fixed width keeps every row aligned.
  const gridCols = 'grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_4rem] gap-2 items-center';

  // The count chip carries the accept/reject-deadline colour (most-urgent order in the bucket),
  // matching the LC Dashboard severities; falls back to the muted / slate default when nothing
  // is urgent. `title` surfaces the reason on hover.
  const countChip = (n: number, opts: { muted?: boolean; urgency?: string; title?: string } = {}) => {
    const urgent = urgencyChipClass(opts.urgency);
    const base = urgent || (opts.muted ? 'text-muted' : 'bg-slate-600 text-white dark:bg-slate-500');
    return (
      <span
        title={opts.title}
        className={`justify-self-end shrink-0 tabular-nums font-semibold rounded px-1.5 text-sm ${base}`}
      >
        {n.toLocaleString()}
      </span>
    );
  };

  const urgencyTitle = (u?: string): string | undefined =>
    u === 'red' ? 'Accept/reject deadline passed'
    : u === 'orange' ? 'Less than 60 min to accept/reject'
    : u === 'yellow' ? 'Less than 120 min to accept/reject'
    : undefined;

  const leafRow = (b: EdiBucket, opts: { indent: boolean; label: string }) => {
    const key = bucketKey(b.edihandle, b.billto);
    const active = selectedKey === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onSelect(b.edihandle, b.billto)}
        title={`View ${opts.label} (${b.edihandle})`}
        className={`${gridCols} w-full text-left text-sm px-2 py-1 transition-colors ${
          active
            ? 'bg-blue-100 dark:bg-blue-900/40'
            : 'hover:bg-blue-50 dark:hover:bg-slate-700/40'
        }`}
      >
        <span className={`truncate ${opts.indent ? 'pl-4 text-gray-600 dark:text-gray-300' : 'font-medium text-body'}`}>
          {opts.label}
        </span>
        <span className="truncate text-gray-600 dark:text-gray-400">
          {b.edicsr || '—'}
          {/* Orders on a lane-scoped handle that no LC's rule claims. Only ever non-zero
              once someone has written rules for this handle, so its presence IS the
              signal — it means work here is falling through to the handle's catch-all
              LCs (or to nobody, if every LC is lane-scoped). Shown on the roster line
              rather than as a second chip so it reads as a caveat about who is covering
              this bucket, which is what it is. */}
          {!!b.unclaimed && (
            <span
              className="ml-1.5 whitespace-nowrap rounded px-1 py-px text-[11px] font-medium text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40"
              title={`${b.unclaimed} order${b.unclaimed === 1 ? '' : 's'} here match no LC's lane rule`}
            >
              {b.unclaimed} unclaimed
            </span>
          )}
        </span>
        {countChip(b.count, { urgency: b.urgency, title: urgencyTitle(b.urgency) })}
      </button>
    );
  };

  return (
    <div
      className={
        embedded
          // Natural height (no frame / inner scroll) so the host tile can grow-to-fit
          // its content like the other dynamic count panels (see LCDashboards fit logic).
          ? (className || '')
          : `rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800 shadow-sm ${className || ''}`
      }
    >
      {!embedded && (
        <div className="bg-slate-800 text-white text-center font-bold text-sm py-1.5">
          {title} {total > 0 && <span className="font-normal opacity-80">— {total.toLocaleString()} total</span>}
        </div>
      )}
      <div className={`${gridCols} bg-slate-700 text-white font-semibold text-xs uppercase tracking-wide px-2 py-1`}>
        <span>Bill To</span>
        <span>LC</span>
        <span className="text-right">#</span>
      </div>

      <div
        className={`divide-y divide-gray-100 dark:divide-gray-700/50 ${
          embedded ? '' : 'max-h-[70vh] overflow-y-auto'
        }`}
      >
        {loading && buckets.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted">Loading…</div>
        )}
        {!loading && buckets.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted">No pending EDI orders.</div>
        )}
        {groups.map((g) =>
          g.rows.length === 1 ? (
            leafRow(g.rows[0], { indent: false, label: g.billtoname })
          ) : (
            (() => {
              const worst = worstUrgency(g.rows.map((r) => r.urgency));
              return (
                <div key={`grp-${g.billto}`}>
                  <div className={`${gridCols} px-2 py-1 bg-gray-50 dark:bg-slate-800/60`}>
                    <span className="truncate font-semibold text-body">{g.billtoname}</span>
                    <span />
                    {countChip(g.total, { muted: !worst, urgency: worst, title: urgencyTitle(worst) })}
                  </div>
                  {g.rows.map((b) => leafRow(b, { indent: true, label: b.edihandle }))}
                </div>
              );
            })()
          )
        )}
      </div>
    </div>
  );
};

export default EdiBucketPanel;
